// The files kept against a record on its page (#575): a customer's, supplier's, job worker's, Item's, Run's or a Run's
// order's. Each is opened from the Application API, with when it was kept, by whom (#818) and the person's own words. The route's per-record
// authorization decides what the list holds: a refused read lists nothing rather than failing the page.
import { useEffect, useState } from "react";
import type { Api, KeptAgainst, KeptFileRow } from "../api.js";
import { formatDate } from "../i18n/format.js";
import { useLanguage, useWords } from "../i18n/words.js";

export function KeptFiles({
  api,
  object,
  record,
  title,
}: {
  api: Required<Pick<Api, "keptFiles">>;
  object: KeptAgainst;
  record: string;
  /** The list's heading; "Files" unless given. */
  title?: string | undefined;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [files, setFiles] = useState<readonly KeptFileRow[] | "refused">();
  // Read again only for another record: the page passes a new `api` object as it renders.
  const { keptFiles } = api;
  useEffect(() => {
    let current = true;
    setFiles(undefined);
    keptFiles(object, record).then(
      (loaded) => {
        if (current) setFiles(loaded);
      },
      () => {
        if (current) setFiles("refused");
      },
    );
    return () => {
      current = false;
    };
  }, [keptFiles, object, record]);
  if (files === undefined || files === "refused") return null;
  const heading = title ?? w.keptFilesTitle;
  return (
    <section className="kept-files" aria-label={heading}>
      <h3>{heading}</h3>
      {files.length === 0 ? (
        <p className="muted">{w.keptFilesNone}</p>
      ) : (
        <ul className="plain-list">
          {files.map((file) => {
            const when = new Date(file.keptAt);
            const keptAt = Number.isNaN(when.getTime())
              ? file.keptAt
              : formatDate(when, language, { dateStyle: "medium", timeStyle: "short" });
            return (
              <li key={file.href}>
                <a href={file.href} download={file.name} aria-label={w.keptFilesOpen(file.name)}>
                  {file.name}
                </a>{" "}
                <span className="small muted">
                  {file.keptBy ? w.keptFilesKeptBy(keptAt, file.keptBy) : w.keptFilesKeptAt(keptAt)}
                </span>
                {file.words ? <p className="small">{file.words}</p> : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
